import { Button, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import moment from 'moment';
import { CSSProperties, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useCurrentUser from 'src/hooks/use-current-user';
import { createPtoRequestAction, resetPtoRequest } from 'src/redux/actions/myAvailabilityAction';

type Props = {
  close: any;
  setSucess: any;
};

function RequestPTO({ close, setSucess }: Props) {
  const modalStyle: CSSProperties = {
    position: 'fixed',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    WebkitTransform: 'translate(-50%, -50%)',
  };
  const modalContainerStyle: CSSProperties = {
    backgroundColor: '#fff',
    width: '600px',
    height: 'auto',
    paddingBottom: '24px',
  };
  const [errors, setErros] = useState({
    description: '',
    startDate: '',
    endDate: '',
  });

  const [values, setValues] = useState({
    description: '',
  });

  const [startDate, setStartDate] = useState<Date>(new Date(new Date().setHours(9, 0, 0, 0)));
  const [endDate, setEndDate] = useState<Date>(new Date(new Date().setHours(17, 0, 0, 0)));
  const dispatch = useDispatch();
  const [providerId, created] = useCurrentUser(['providerId', 'created']);
  const getCretedAptResponse = useSelector((state: any) => state.createPtoRequestReducer);

  const [selectedHours, setSelectedHours] = useState({
    startTime: moment(new Date().setHours(9, 0, 0, 0)).format('HH:mm:ss'),
    endTime: moment(new Date().setHours(17, 0, 0, 0)).format('HH:mm:ss'),
  });

  const handleOnchange = (e: any) => {
    const name = e.target.name;
    const value = e.target.value;

    setValues((prevState: any) => ({
      ...prevState,
      [name]: value,
    }));
    setErros({
      description: '',
      startDate: '',
      endDate: '',
    });
  };

  const [checkedAllDay, setCheckedAllDay] = useState(false);
  const toggleAllDay = () => {
    setErros((prevState) => ({
      ...prevState,
      endDate: '',
    }));
    setCheckedAllDay((prev) => !prev);
  };
  const [createPtoRequestError, setCreatePtoRequestError] = useState('');

  const submitRequestPTO = (e: any) => {
    e.preventDefault();
    if (!handleValidInput()) {
      if (checkedAllDay) {
        setStartDate(new Date(startDate.setHours(0, 0, 0, 0)));
        setEndDate(new Date(endDate.setHours(0, 0, 0, 0)));
        startDate.setDate(startDate.getDate() + 1);
        endDate.setDate(endDate.getDate() + 1);
      } else {
        setStartDate(new Date(startDate.setMinutes(0, 0)));
        setEndDate(new Date(endDate.setMinutes(0, 0)));
      }

      const startDateWithTime = startDate.toISOString();
      const endDateWithTime = endDate.toISOString();
      const [datePartStart, _] = startDateWithTime.split('T');
      const [datePartEnd, __] = endDateWithTime.split('T');
      const updatedStartTime = `${datePartStart}T${selectedHours.startTime}.000Z`;
      const updatedEndTime = `${datePartEnd}T${selectedHours.endTime}.000Z`;
      dispatch(
        createPtoRequestAction(
          {
            description: values.description,
            startDate: updatedStartTime,
            endDate: updatedEndTime,
            allDay: checkedAllDay,
          },
          typeof providerId === 'string' ? providerId : ''
        )
      );
    }
  };

  const handleValidInput = () => {
    let validation = false;

    if (values.description === '') {
      validation = true;
      setErros((prevState: any) => ({
        ...prevState,
        description: 'Please enter a description',
      }));
    }

    if (!checkedAllDay && startDate && endDate && startDate > endDate) {
      validation = true;
      setErros((prevState: any) => ({
        ...prevState,
        endDate: 'End date time should not be prior of Start date time',
      }));
    }

    if (checkedAllDay && startDate && endDate && startDate > endDate) {
      validation = true;
      setErros((prevState: any) => ({
        ...prevState,
        endDate: 'Please choose valid end date',
      }));
    }

    const date1 = moment(startDate);
    const date2 = moment(endDate);
    if (!checkedAllDay && date1?.isSame(date2)) {
      validation = true;
      setErros((prevState: any) => ({
        ...prevState,
        endDate: 'Start time and end time should not be same.',
      }));
    }
    return validation;
  };

  useEffect(() => {
    if (!getCretedAptResponse.isCreated && getCretedAptResponse.response === null) return;

    if (!getCretedAptResponse.isCreated && getCretedAptResponse.response !== null) {
      setCreatePtoRequestError(getCretedAptResponse.response?.message);
      dispatch(resetPtoRequest());
      return;
    }
    dispatch(resetPtoRequest());
    setSucess(true);
  }, [getCretedAptResponse]);

  return (
    <form onSubmit={(e) => submitRequestPTO(e)}>
      <Box style={modalStyle}>
        <Box style={modalContainerStyle}>
          <Stack>
            <Box sx={{ padding: '24px 24px 24px 24px' }}>
              <Typography variant="h6">Requests a PTO </Typography>
            </Box>
            <Stack
              spacing={3}
              sx={{ padding: '24px 24px 24px 24px' }}
            >
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={values.description}
                onChange={(e) => handleOnchange(e)}
                error={errors.description !== ''}
                helperText={errors.description}
              />
              <div>
                <FormControlLabel
                  control={
                    <Switch
                      name="allDay"
                      onChange={toggleAllDay}
                      checked={checkedAllDay}
                    />
                  }
                  label="All day"
                />
              </div>
              <DateTimePicker
                onChange={(value) => {
                  setErros({
                    description: '',
                    startDate: '',
                    endDate: '',
                  });
                  const inputTime = moment(value, 'ddd MMM DD YYYY HH:mm:ss ZZ');
                  const formattedTime = inputTime?.format('HH:mm:ss');
                  setSelectedHours((prev) => ({
                    ...prev,
                    startTime: formattedTime,
                  }));
                  setStartDate(value || new Date());
                }}
                label="Start date"
                value={startDate}
                slotProps={{
                  textField: {
                    name: 'startDate',
                  },
                }}
                views={checkedAllDay ? ['day', 'year'] : undefined}
                minutesStep={60}
                minDate={new Date(created as string)}
              />
              <DateTimePicker
                onChange={(value) => {
                  setErros({
                    description: '',
                    startDate: '',
                    endDate: '',
                  });
                  const inputTime = moment(value, 'ddd MMM DD YYYY HH:mm:ss ZZ');
                  const formattedTime = inputTime?.format('HH:mm:ss');
                  setSelectedHours((prev) => ({
                    ...prev,
                    endTime: formattedTime,
                  }));
                  setEndDate(value || new Date());
                }}
                minDate={new Date(created as string)}
                label="End date"
                value={endDate}
                views={checkedAllDay ? ['day', 'year'] : undefined}
                slotProps={{
                  textField: {
                    name: 'endDate',
                    error: errors.endDate !== '',
                    helperText: errors.endDate,
                  },
                }}
                minutesStep={60}
              />
              {createPtoRequestError && (
                <Typography
                  color={'error'}
                  sx={{ fontSize: '14px' }}
                >
                  {createPtoRequestError}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              marginRight: '24px',
            }}
          >
            <Box sx={{ flexGrow: 1 }} />
            <Button
              color="inherit"
              onClick={close}
            >
              Cancel
            </Button>
            <Button
              sx={{ ml: 1 }}
              type="submit"
              variant="contained"
            >
              Send My Request
            </Button>
          </Box>
        </Box>
      </Box>
    </form>
  );
}

export default RequestPTO;
